// The gadgets' muzzle fire and cartridge meshes (`AngledGadget::GraphicUpdate` 0xcdd590; crates/sim/
// src/weapons/gadgets/angled_gadget.rs). The sim records each operation on the gadgets'
// `XMeshInstance`s as an EFFECT_MESH event; this module holds the instances and carries the
// operations out in step order:
// - FX_INIT (`InitialiseEffectMesh` 0xcdcff0): a new, invisible, unattached instance of the mesh
//   (`GRM::CreateInstance` on `g_pLayerNames[3]`, the GameMID layer) replaces the old one.
// - FX_PLAY: the muzzle is attached to the target's locator (`WormPoser::AttachMeshToGadget` on
//   the worm's held gadget mesh, `BaseMesh::AttachThatToThis` on a vehicle mesh); a cartridge is
//   placed at the locator's world position (`GetLocatorPositionFromResourceMesh`: the origin
//   when the worm holds no gadget mesh; `QueryNodeTransform` on the vehicle mesh), keeping its
//   orientation. Then "Go" plays from the step (speed 1, not looping: the end pose holds) and it
//   is shown.
// - FX_HIDE hides (with FX_WORM the muzzle first leaves that worm's gadget mesh); FX_RELEASE
//   drops the instance.
// An instance plays "Go" at (frame tick - start tick) / 60 s, clamped to the clip.
import * as THREE from 'three';
import { findMesh, instantiate, loadTemplate, poseLayers, type MeshInstance } from './meshes.ts';
import { EFFECT_MESH, FX_HIDE, FX_INIT, FX_PLAY, FX_RELEASE, FX_VEHICLE, FX_WORM, type EffectMeshEvent, type SoundEvent } from '../sim/view.ts';

/** One effect mesh: its instance (null while loading) and the state the sim gave it. */
type Fx = {
  inst: MeshInstance | null;
  /** The node it is attached to (null: unattached, a child of the scene root). */
  parent: THREE.Object3D | null;
  /** The world position of an unattached instance (`XResourceInstance::SetPosition`). */
  pos: THREE.Vector3;
  visible: boolean;
  /** The step "Go" started at (-1: never). */
  start: number;
};

const GO = 'Go';

export class GunEffects {
  private fx = new Map<string, Fx>();

  /** The scene the unattached instances live in (the objects pass). */
  private root: THREE.Object3D;

  constructor(root: THREE.Object3D) { this.root = root; }

  /** Carries out the EFFECT_MESH events of the steps since the last update, in order, then poses
   * every instance at frame `tick`. `gadgetOf(worm)` is the worm's held gadget mesh, `meshOf(id)`
   * the instance of BaseMesh `id` (null when there is none yet). */
  update(tick: number, events: SoundEvent[], gadgetOf: (worm: number) => MeshInstance | null, meshOf: (id: number) => MeshInstance | null, theme: string | null): void {
    for (const e of events) if (e.kind === EFFECT_MESH && e.fx) this.apply(e.fx, gadgetOf, meshOf, theme);
    for (const f of this.fx.values()) {
      const inst = f.inst;
      if (!inst) continue;
      const parent = f.parent ?? this.root;
      if (inst.root.parent !== parent) parent.add(inst.root);
      if (!f.parent) inst.root.position.copy(f.pos);
      inst.root.visible = f.visible;
      if (f.start >= 0) {
        const clip = inst.template.anim?.clips.find(c => c.name === GO);
        const time = Math.max(0, Math.min((tick - f.start) / 60, clip?.duration ?? 0));
        poseLayers(inst, [{ clip: GO, time, weight: 1 }]);
      }
      inst.root.updateMatrixWorld(true);
    }
  }

  private apply(e: EffectMeshEvent, gadgetOf: (worm: number) => MeshInstance | null, meshOf: (id: number) => MeshInstance | null, theme: string | null): void {
    const key = `${e.gadget}/${e.slot}`;
    if (e.op === FX_INIT) {
      this.release(key);
      const f: Fx = { inst: null, parent: null, pos: new THREE.Vector3(), visible: false, start: -1 };
      this.fx.set(key, f);
      const src = findMesh(e.name, null, theme);
      if (src) void loadTemplate(src.glb, src.wanim).then(t => { if (t && this.fx.get(key) === f) f.inst = instantiate(t); });
      return;
    }
    const f = this.fx.get(key);
    if (!f) return;
    if (e.op === FX_RELEASE) this.release(key);
    else if (e.op === FX_HIDE) {
      const gadget = e.target === FX_WORM ? gadgetOf(e.object) : null;
      if (gadget && f.parent && isWithin(f.parent, gadget.root)) f.parent = null;
      f.visible = false;
    } else if (e.op === FX_PLAY) {
      const holder = e.target === FX_WORM ? gadgetOf(e.object) : e.target === FX_VEHICLE ? meshOf(e.object) : null;
      const node = holder ? holder.root.getObjectByName(e.name) ?? null : null;
      if (e.slot === 0) {
        // The muzzle: attached to the locator (a vehicle mesh without an instance detaches it).
        if (holder) f.parent = node ?? holder.root;
        else if (e.target === FX_VEHICLE) f.parent = null;
      } else if (e.target === FX_WORM) {
        f.pos.set(0, 0, 0);
        if (node) { node.updateWorldMatrix(true, false); node.getWorldPosition(f.pos); }
      } else if (e.target === FX_VEHICLE && node) {
        node.updateWorldMatrix(true, false);
        node.getWorldPosition(f.pos);
      }
      f.start = e.tick;
      f.visible = true;
    }
  }

  private release(key: string): void {
    this.fx.get(key)?.inst?.root.removeFromParent();
    this.fx.delete(key);
  }
}

/** Whether `o` is `ancestor` or below it. */
function isWithin(o: THREE.Object3D, ancestor: THREE.Object3D): boolean {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p === ancestor) return true;
  return false;
}
